import { roleLabel, type WorkspaceSummary } from '@nolens/domain';
import { Banner, Button, Dialog, Field, Input, useLocale } from '@nolens/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../lib/api';
import { signOut } from '../lib/session';
import { spaceName } from '../lib/locale';
import { accountText } from '../i18n/account';
import { usePlanState } from './settings';
import { WithMe } from './workspace';
import { shellText } from '../i18n/shell';

/** A conta: email e apagar a conta (DELETE /v1/me). */
export function AccountPage() {
  const locale = useLocale();
  const t = accountText(locale);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const remove = useMutation({
    mutationFn: () => api('/v1/me', { method: 'DELETE' }),
    onSuccess: () => void signOut(),
  });
  const st = shellText(locale);
  return (
    <WithMe settings crumbs={() => [{ label: st.account }]}>
      {(me) => {
        const email = me.user.email ?? '';
        const personal = me.workspaces.find((w) => w.kind === 'personal');
        const teams = me.workspaces.filter((w) => w.kind === 'team');
        const close = () => {
          setOpen(false);
          setTyped('');
          remove.reset();
        };
        return (
          <section className="max-w-xl">
            <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
            <p className="mt-2 text-[15px] text-text-2">
              {t.signedInAs} <strong className="text-text-1">{email}</strong>
            </p>
            <div className="mt-8">
              <h2 className="font-semibold">{t.plansTitle}</h2>
              <p className="mt-1 text-[14px] text-text-2">{t.plansHint}</p>
              <ul
                className="mt-3 divide-y divide-border rounded-[14px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]"
                data-account-plans
              >
                {[...(personal ? [personal] : []), ...teams].map((w) => (
                  <PlanRow key={w.id} ws={w} />
                ))}
              </ul>
            </div>
            <div
              className="mt-8 rounded-[14px] p-5 shadow-[inset_0_0_0_1px_var(--critical)]"
              data-delete-account
            >
              <h2 className="font-semibold">{t.deleteTitle}</h2>
              <p className="mt-1 text-[14px] text-text-2">{t.deleteHint}</p>
              <Button variant="danger" className="mt-4" onClick={() => setOpen(true)}>
                {t.deleteButton}
              </Button>
            </div>
            <Dialog
              open={open}
              onClose={close}
              title={t.confirmTitle}
              footer={
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={close}>
                    {t.cancel}
                  </Button>
                  <Button
                    variant="danger"
                    loading={remove.isPending}
                    disabled={typed.trim().toLowerCase() !== email.toLowerCase()}
                    onClick={() => remove.mutate()}
                  >
                    {t.confirm}
                  </Button>
                </div>
              }
            >
              <div className="grid gap-4 text-[14px]">
                <div>
                  <h3 className="font-semibold">{t.goes}</h3>
                  <ul className="mt-1 list-disc pl-5 text-text-2">
                    {personal && <li>{t.goesPersonal(personal.guides)}</li>}
                    {teams
                      .filter((w) => w.members <= 1)
                      .map((w) => (
                        <li key={w.id}>{t.goesTeam(spaceName(w, locale))}</li>
                      ))}
                  </ul>
                </div>
                {teams.some((w) => w.members > 1) && (
                  <div>
                    <h3 className="font-semibold">{t.stays}</h3>
                    <ul className="mt-1 list-disc pl-5 text-text-2">
                      {teams
                        .filter((w) => w.members > 1)
                        .map((w) => (
                          <li key={w.id}>{t.staysTeam(spaceName(w, locale))}</li>
                        ))}
                    </ul>
                  </div>
                )}
                <p className="text-text-2">{t.extensionNote}</p>
                <Field label={t.typeEmail(email)}>
                  {(p) => (
                    <Input
                      {...p}
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      autoComplete="off"
                      data-confirm-email
                    />
                  )}
                </Field>
                {remove.error instanceof ApiError && (
                  <Banner tone="critical">{remove.error.message}</Banner>
                )}
              </div>
            </Dialog>
          </section>
        );
      }}
    </WithMe>
  );
}

/** Um espaço e o seu plano, na página «Conta». */
function PlanRow({ ws }: { ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = accountText(locale);
  const s = usePlanState(ws);
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3" data-plan-row={ws.kind}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium">
          {spaceName(ws, locale)}
          {ws.kind === 'team' && (
            <span className="font-normal text-text-3"> · {roleLabel(ws.role, locale)}</span>
          )}
        </p>
        {s && <p className="text-[13px] text-text-2">{s.state}</p>}
      </div>
      {s && (
        <span className="rounded-full bg-surface-2 px-2.5 py-0.5 text-[13px] font-medium">
          {s.label}
        </span>
      )}
      <Link
        to={`/w/${ws.id}/definicoes/plano`}
        className="text-[13px] font-medium text-accent-text hover:underline"
      >
        {t.seePlan}
      </Link>
    </li>
  );
}
